import { useState } from 'react'
import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { writeErrorMessage } from '../api/problems'
import { useIsSuperAdmin } from '../api/tier'
import { ConfirmDialog } from '../components/ConfirmDialog'
import './screens.css'

interface Membership {
  workspace_id: string
  workspace_name: string | null
  role: string
}
interface AdminUser {
  id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  created_at: string | null
  last_sign_in_at: string | null
  memberships: Membership[]
}
interface UsersResponse {
  users: AdminUser[]
  page: number
  per_page: number
  has_more: boolean
}

// POST /admin/users/{user_id}/password-reset-link (account-basics D37; super_admin +
// CSRF). The response carries a single-use link that is shown ONCE and never stored.
interface ResetLinkResponse {
  user_id: string
  action_link: string
  reset_url: string
  emailed: false
  note?: string
}
interface ShownLink {
  email: string
  url: string
}

const PER_PAGE = 50

function fullName(u: AdminUser): string {
  const n = [u.first_name, u.last_name].filter(Boolean).join(' ')
  return n || '—'
}

export function UsersView() {
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState('')

  const query = useQuery<UsersResponse>({
    queryKey: ['admin-users', page],
    queryFn: () =>
      apiFetch<UsersResponse>(`/admin/users?page=${page}&per_page=${PER_PAGE}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  const { isSuperAdmin } = useIsSuperAdmin()
  const [confirmUser, setConfirmUser] = useState<AdminUser | null>(null)
  // The one-time link lives only in this component state: not in the query cache, not
  // in storage. Dismiss (or generating another) drops it.
  const [shown, setShown] = useState<ShownLink | null>(null)
  const [copyMsg, setCopyMsg] = useState<string | null>(null)

  const resetLink = useMutation({
    mutationFn: (userId: string) =>
      apiFetch<ResetLinkResponse>(
        `/admin/users/${encodeURIComponent(userId)}/password-reset-link`,
        { method: 'POST' },
      ),
    // Drop the finished mutation (and its response) from the mutation cache at once.
    gcTime: 0,
  })
  useWriteForbiddenGuard(resetLink.error)

  const generate = (u: AdminUser) => {
    setShown(null)
    setCopyMsg(null)
    resetLink.mutate(u.id, {
      onSuccess: (data) => {
        setShown({ email: u.email ?? u.id, url: data.reset_url })
        resetLink.reset()
      },
    })
  }

  const dismiss = () => {
    setShown(null)
    setCopyMsg(null)
  }

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopyMsg('Copied.')
    } catch {
      setCopyMsg('Copy failed: select the link and copy it manually.')
    }
  }

  const cols = isSuperAdmin ? 6 : 5

  const users = query.data?.users ?? []
  const hasMore = query.data?.has_more ?? false
  const needle = filter.trim().toLowerCase()
  const rows = needle
    ? users.filter(
        (u) =>
          (u.email ?? '').toLowerCase().includes(needle) ||
          fullName(u).toLowerCase().includes(needle),
      )
    : users

  return (
    <section className="scr" aria-labelledby="users-heading">
      <div className="screen-head">
        <h2 id="users-heading">Users</h2>
        <input
          className="ws-search"
          type="search"
          aria-label="Filter this page of users"
          placeholder="Filter this page…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load users.
        </p>
      )}

      {shown && (
        <div className="scr-panel" role="region" aria-label="One-time reset link">
          <h3>Password reset link for {shown.email}</h3>
          <p className="scr-note">
            Shown once: it is not stored and cannot be shown again. Single use; share it with
            the user privately. Nothing was emailed.
          </p>
          <div className="scr-once">
            <input
              className="scr-input"
              type="text"
              readOnly
              aria-label="Reset link"
              value={shown.url}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button type="button" className="screen-btn" onClick={() => copy(shown.url)}>
              Copy link
            </button>
            <button type="button" className="screen-btn" onClick={dismiss}>
              Dismiss
            </button>
            {copyMsg && (
              <span className={copyMsg === 'Copied.' ? 'scr-ok' : 'scr-err'}>{copyMsg}</span>
            )}
          </div>
        </div>
      )}
      {resetLink.isError && (
        <p className="scr-err" role="alert">
          Reset link failed: {writeErrorMessage(resetLink.error)}
        </p>
      )}

      <table className="scr-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Memberships</th>
            <th>Created</th>
            <th>Last sign-in</th>
            {isSuperAdmin && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id}>
              <td>{u.email ?? '—'}</td>
              <td>{fullName(u)}</td>
              <td>
                {u.memberships.length === 0 ? (
                  <span className="scr-muted">none</span>
                ) : (
                  <span className="scr-chips">
                    {u.memberships.map((m) => (
                      <span key={m.workspace_id} className="scr-chip">
                        {m.workspace_name ?? m.workspace_id.slice(0, 8)} · {m.role}
                      </span>
                    ))}
                  </span>
                )}
              </td>
              <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
              <td>
                {u.last_sign_in_at
                  ? new Date(u.last_sign_in_at).toLocaleDateString()
                  : '—'}
              </td>
              {isSuperAdmin && (
                <td>
                  <button
                    type="button"
                    className="screen-btn"
                    aria-label={`Generate reset link for ${u.email ?? u.id}`}
                    disabled={resetLink.isPending}
                    onClick={() => setConfirmUser(u)}
                  >
                    Generate reset link
                  </button>
                </td>
              )}
            </tr>
          ))}
          {!query.isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={cols} className="ws-empty">
                {needle ? 'No users match this filter on the current page.' : 'No users.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </button>
        <span className="ws-count">Page {page}</span>
        <button type="button" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </div>
      <p className="scr-note">
        The filter box narrows the current page only. Use Previous/Next to page through all users.
      </p>

      {confirmUser && (
        <ConfirmDialog
          label="Confirm reset link"
          confirmText="Generate"
          onCancel={() => setConfirmUser(null)}
          onConfirm={() => {
            const u = confirmUser
            setConfirmUser(null)
            generate(u)
          }}
        >
          <p>
            Generate a one-time password reset link for <strong>{confirmUser.email ?? confirmUser.id}</strong>?
            Nothing is emailed; the link is shown here once for you to share privately. The
            action is audited.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
